import React, { useEffect, useRef } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import { t } from 'i18next';

// Initialize the highcharts-more module
HighchartsMore(Highcharts);

interface LivisAreaRangeChartProps {
    rangeData: [number, number][];
    averageData: number[];
    title?: string;
    subtitle?: string;
    height?: number;
    rangeName?: string;
    averageName?: string;
    rangeColor?: string;
    lineColor?: string;
    fillOpacity?: number;
    valueSuffix?: string;
    pointStart?: string;
    pointIntervalUnit?: 'day' | 'month' | 'year';
    xAxisType?: 'datetime' | 'linear' | 'logarithmic' | 'category';
    showTooltip?: boolean;
    showLegend?: boolean;
    categories?: string[];
    onClickPoint?: (point: any) => void;
    noDataMessage?: string; // NEW: Custom no data message
    x_title?: string;
    y_title?: string;
}

const LivisAreaRangeChart: React.FC<LivisAreaRangeChartProps> = ({
    rangeData = [],
    averageData = [],
    title = '',
    subtitle = '',
    height = 400,
    rangeName = 'Range',
    averageName = 'Average',
    rangeColor = '#6366F1',
    lineColor = '#4F46E5',
    fillOpacity = 0.3,
    valueSuffix = '',
    pointStart = undefined,
    pointIntervalUnit = 'day',
    xAxisType = 'category',
    showTooltip = true,
    showLegend = true,
    categories = [],
    onClickPoint,
    noDataMessage = "No Record Found", // NEW: Default message
    x_title = '',
    y_title = '',
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<Highcharts.Chart | null>(null);

    // // NEW: Check for empty data and render no data message
    // if ((!rangeData || rangeData.length === 0) && (!averageData || averageData.length === 0)) {
    //     return (
    //         <div 
    //             className="livis-dashboard-no-record-centered"
    //             style={{ 
    //                 width: '100%', 
    //                 height: `${height}px`, 
    //                 display: 'flex', 
    //                 alignItems: 'center', 
    //                 justifyContent: 'center' 
    //             }}
    //         >
    //             <p>{t(noDataMessage)}</p>
    //         </div>
    //     );
    // }

     const NoDataTemplate = () => (
            <div
                className="livis-dashboard-no-record-centered"
                style={{
                    width: '100%',
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <p>{t(noDataMessage)}</p>
            </div>
        );

    useEffect(() => {
        if (!chartRef.current) return;

        // Destroy existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Configure x-axis based on type
        const xAxisConfig: Highcharts.XAxisOptions = {
            title: {
                text: x_title,
                style: {
                    fontSize: '14px',
                    fontWeight: '600'
                }   
            },
            type: xAxisType,
            gridLineWidth: 1,
            gridLineColor: '#f1f5f9',
            lineColor: '#e2e8f0',
            tickColor: '#e2e8f0',
            labels: {
                style: {
                    color: '#64748b',
                    fontSize: '12px'
                }
            }
        };

        // Add categories if provided and type is category
        if (xAxisType === 'category' && categories && categories.length > 0) {
            xAxisConfig.categories = categories;
        }

        // Configure plot options based on x-axis type
        const plotOptions: Highcharts.PlotOptions = {
            series: {
                cursor: onClickPoint ? 'pointer' : 'default',
                point: {
                    events: {
                        click: function() {
                            if (onClickPoint) {
                                onClickPoint(this);
                            }
                        }
                    }
                }
            }
        };

        // Add point configuration for datetime
        if (xAxisType === 'datetime' && pointStart) {
            plotOptions.series = {
                ...plotOptions.series,
                pointStart: typeof pointStart === 'string' ? Date.parse(pointStart) : pointStart,
                pointIntervalUnit: pointIntervalUnit
            };
        }

        const chartOptions: Highcharts.Options = {
            chart: {
                type: 'line',
                height: height,
                backgroundColor: 'transparent',
                spacing: [10, 10, 15, 10]
            },
            title: {
                text: title,
                align: 'left',
                style: {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b'
                }
            },
            subtitle: {
                text: subtitle,
                align: 'left',
                style: {
                    fontSize: '12px',
                    color: '#64748b'
                }
            },
            xAxis: xAxisConfig,
            yAxis: {
                title: {
                    text: y_title,
                    style: {
                        fontSize: '14px',
                        fontWeight: '600'
                    }
                },
                gridLineWidth: 1,
                gridLineColor: '#f1f5f9',
                lineColor: '#e2e8f0',
                tickColor: '#e2e8f0',
                labels: {
                    style: {
                        color: '#64748b',
                        fontSize: '12px'
                    }
                }
            },
            tooltip: {
                enabled: showTooltip,
                shared: true,
                valueSuffix: valueSuffix,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e2e8f0',
                borderRadius: 8,
                shadow: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 3,
                    offsetX: 1,
                    offsetY: 1
                },
                style: {
                    fontSize: '12px',
                    color: '#374151'
                }
            },
            legend: {
                enabled: showLegend,
                align: 'center',
                verticalAlign: 'bottom',
                borderWidth: 0,
                itemStyle: {
                    fontSize: '12px',
                    color: '#64748b'
                }
            },
            plotOptions: plotOptions,
            credits: {
                enabled: false
            },
            series: [
                {
                    name: averageName,
                    data: averageData,
                    type: 'line',
                    zIndex: 1,
                    color: lineColor,
                    lineWidth: 2,
                    marker: {
                        enabled: true,
                        fillColor: 'white',
                        lineWidth: 2,
                        lineColor: lineColor,
                        radius: 4,
                        states: {
                            hover: {
                                radius: 6
                            }
                        }
                    }
                },
                {
                    name: rangeName,
                    data: rangeData,
                    type: 'arearange',
                    lineWidth: 0,
                    linkedTo: ':previous',
                    color: rangeColor,
                    fillOpacity: fillOpacity,
                    zIndex: 0,
                    marker: {
                        enabled: false
                    },
                    enableMouseTracking: true
                }
            ]
        };

        // Create the chart
        chartInstance.current = Highcharts.chart(chartRef.current, chartOptions);

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [
        rangeData,
        averageData,
        title,
        subtitle,
        height,
        rangeName,
        averageName,
        rangeColor,
        lineColor,
        fillOpacity,
        valueSuffix,
        pointStart,
        pointIntervalUnit,
        xAxisType,
        showTooltip,
        showLegend,
        categories,
        onClickPoint
    ]);

    if ((!rangeData || rangeData.length === 0) && (!averageData || averageData.length === 0)) {
        return <NoDataTemplate />;
    }

    return <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />;
};

export default LivisAreaRangeChart;